<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContactCard',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    item: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );

  const act_7315092382398562_reloadComponentData_z4uepr224 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_j0ub063xh =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_z4uepr224,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_j0ub063xh],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_j0ub063xh,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_j0ub063xh = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_j0ub063xh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpBox direction="column">
      <ItpText strong :text="item.name" spacing="mb-2"> </ItpText>
      <ItpBox spacing="mb-2" alignItems="center" v-if="item.job_title">
        <Icon
          icon="mdi:work-outline"
          :title="pgettext('Contact', 'Job title')"
          class="me-2"
        >
        </Icon>
        <ItpText :text="item.job_title" v-if="item.job_title"> </ItpText>
      </ItpBox>
      <ItpBox spacing="mb-2" alignItems="center" v-if="item.phone">
        <Icon
          icon="ic:baseline-phone"
          :title="pgettext('Contact', 'Phone Number')"
          class="me-2"
        >
        </Icon>
        <ItpText :text="item.phone"> </ItpText>
      </ItpBox>
      <ItpBox spacing="mb-2" alignItems="center" v-if="item.mobile">
        <Icon
          icon="mdi:cellphone-basic"
          :title="pgettext('Contact', 'Mobile')"
          class="me-2"
        >
        </Icon>
        <ItpText :text="item.mobile"> </ItpText>
      </ItpBox>
      <ItpBox spacing="mb-2" alignItems="center" v-if="item.fax">
        <Icon icon="mdi:fax" :title="pgettext('Contact', 'Fax')" class="me-2">
        </Icon>
        <ItpText :text="item.fax"> </ItpText>
      </ItpBox>
      <ItpBox spacing="mb-2" alignItems="center" v-if="item.email">
        <Icon icon="mdi:at" :title="pgettext('Contact', 'Email')" class="me-2">
        </Icon>
        <ItpText :text="item.email"> </ItpText>
      </ItpBox>
      <ItpBox v-if="item.notes">
        <Icon
          icon="solar:notes-linear"
          :title="pgettext('Contact', 'Notes')"
          class="me-2"
        >
        </Icon>
        <ItpText :text="item.notes" pre> </ItpText>
      </ItpBox>
    </ItpBox>
  </ItpUsercontrol>
</template>
